<template>
  <span>
    <main-loader v-if="!isFetched && !isDisabled" />
    <admin-wrapper
      :straight-title="additionalModuleName"
      :inputs="inputs"
      @save="saveCard"
      :isCustomSaving="isConfirmButtonLoading"
      :is-fetched="isFetched"
      @change="editSingleSettingHandler"
      v-if="!isFetching"
    >
      <template #title.input>
        <v-row no-gutters>
          <v-col class="assignInspector">
            {{ nameAttribute ? $i18n.t('admin.assignInspector') : '' }}
          </v-col>
        </v-row>
      </template>
      <template #district_datasource.input="data">
        <v-col cols="3">
          <data-input v-bind="data" v-model="datasource" @change="clearInputs" />
        </v-col>
      </template>
      <template #name_attribute.input="data">
        <v-col cols="3">
          <data-input v-bind="data" v-model="nameAttribute" @change="clearInputs(true)" :loading="isNamesLoading" />
        </v-col>
      </template>
      <template #districts.input>
        <v-col cols="12">
          <template v-if="nameAttribute && !isNamesLoading">
            <admin-input-row
              :straight-text="input.name"
              name="district"
              align="baseline"
              v-for="input in computedDistrictInputs"
              :key="input.name"
            >
              <template #district.input>
                <v-col cols="6">
                  <data-input
                    :dataType="{ name: 'combobox' }"
                    multiple
                    outlined
                    dense
                    append-icon=""
                    chips
                    item-text="user_name"
                    :disabled="isDisabled"
                    :return-object="true"
                    :items="usersArray"
                    v-model="districtItems[input.district_id]"
                    deletable-chips
                  >
                  </data-input>
                </v-col>
              </template>
            </admin-input-row>
          </template>
          <main-loader v-if="isNamesLoading" />
        </v-col>
      </template>
    </admin-wrapper>
  </span>
</template>

<script>
import AdminWrapper from '@/components/AdminWrapper';
import AdminInputRow from '@/components/AdminInputRow';
import DataInput from '@/components/DataInput';

import { cloneDeep } from 'lodash';
import { call, get } from 'vuex-pathify';
import MainLoader from '@/components/MainLoader';

export default {
  name: 'ZDMInspectors',
  components: { MainLoader, AdminWrapper, AdminInputRow, DataInput },
  data: () => ({
    isConfirmButtonLoading: false,
    isFetching: false,
    isFetched: false,
    isNamesLoading: false,
    districtItems: {},
    features: undefined,
    datasource: undefined,
    nameAttribute: undefined,
  }),
  computed: {
    additionalModuleName: get('admin/additionalModules.ZDM_INSPECTORS.verbose_name'),
    isModuleEnabled: get('admin/settingsValues@zdm_inspectors_module_enabled'),
    inspectors: get('zdm_inspectors/inspectors'),
    users: get('users/users'),
    dataSources: get('layers/metadata'),
    isDisabled() {
      return !this.isModuleEnabled;
    },
    usersArray() {
      return this.$_objectToArray(this.users).map(user => {
        return {
          user_id: user.id,
          user_name: user.name,
        };
      });
    },

    computedDistrictInputs() {
      return this.computedFeatures.map(value => {
        return {
          ...value,
          name: value[this.nameAttribute],
          isCustom: true,
        };
      });
    },
    inputs() {
      return [
        {
          name: 'zdm_inspectors_module_enabled',
          hasSuffix: false,
          straightText: this.additionalModuleName,
          dataType: {
            name: 'switch',
          },
          subtitle: this.$i18n.t('admin.moduleEnablerSubtitle', { suffix: this.additionalModuleName }),
        },
        {
          name: 'district_datasource',
          translationPath: 'admin.zdmInspectorsDistrictTitle',
          dataType: {
            name: 'select',
          },
          outlined: true,
          dense: true,
          align: 'start',
          itemValue: 'name',
          itemText: 'verbose_name',
          items: this.sortedPolygonDataSources,
          subtitle: 'admin.zdmInspectorsDistrictSubtitle',
          disabled: this.isDisabled,
        },
        {
          name: 'name_attribute',
          translationPath: 'admin.zdmInspectorsNameAttrTitle',
          dataType: {
            name: 'select',
          },
          outlined: true,
          dense: true,
          align: 'start',
          items: this.datasource ? this.sortedSelectedDatasourceAttributes : [],
          subtitle: 'admin.zdmInspectorsNameAttrSubtitle',
          disabled: this.isDisabled ? this.isDisabled : !this.datasource,
          itemText: 'verbose_name',
          itemValue: 'name',
        },
        {
          name: 'title',
          translationPath: 'admin.failureAvailableTags',
          hasSuffix: false,
          isCustom: true,
          isNamedSlot: false,
          outlined: true,
        },
        {
          name: 'districts',
          hasSuffix: false,
          isCustom: true,
          isNamedSlot: false,
          outlined: true,
        },
      ];
    },
    sortedPolygonDataSources() {
      return this.$_objectToArray(this.dataSources).filter(dataSource => dataSource.geometry_type === 'polygon');
    },
    selectedDatasource() {
      return this.dataSources[this.datasource];
    },
    attributesSchema() {
      return this.datasource ? this.selectedDatasource.attributes_schema : undefined;
    },
    sortedSelectedDatasourceAttributes() {
      const geom_name = this.attributesSchema.geometry_name;
      return this.attributesSchema.attributes.filter(v => v.verbose_name !== geom_name);
    },
    computedFeatures() {
      return this.features === undefined
        ? []
        : this.features?.features.map(value => {
            return {
              [this.attributesSchema.id_name]: value.id.toString(),
              district_id: value.id,
              ...value.properties,
            };
          });
    },
  },
  methods: {
    getUsers: call('users/getUsers'),
    editSingleSetting: call('admin/editSingleSetting'),
    getDataSources: call('layers/getLayersMetadata'),
    getInspectors: call('zdm_inspectors/getInspectors'),
    editInspectors: call('zdm_inspectors/editInspectors'),
    getDataSourceFeatures: call('layers/getDataSourceFeatures'),
    getDatasourceSettings: call('datasourceSettings/getDatasourceSettings'),
    editDatasourceSettings: call('datasourceSettings/editDatasourceSettings'),
    editSingleSettingHandler(value, name) {
      if (name === 'zdm_inspectors_module_enabled') {
        this.editSingleSetting({
          key: name,
          body: {
            value,
          },
        });
      }
    },
    async clearInputs(onlyname = false) {
      if (onlyname === true) {
        this.districtItems = {};
        return;
      }
      this.isNamesLoading = true;
      this.features = await this.getDataSourceFeatures({
        dataSource: this.datasource,
        with_geometry: false,
      });
      this.nameAttribute = '';
      this.isNamesLoading = false;
    },
    async saveCard() {
      const inspectorsPayload = Object.keys(this.districtItems).map(key => {
        return {
          district_id: Number(key),
          users_ids: this.districtItems[key].map(v => v.user_id),
        };
      });
      try {
        this.isConfirmButtonLoading = true;
        await this.editInspectors(inspectorsPayload);
        if (this.datasource !== undefined) {
          await this.editDatasourceSettings({
            name: 'zdm_inspectors_district',
            settings: {
              datasource: this.datasource,
              settings: {
                district_name: this.nameAttribute,
              },
            },
          });
        }
      } catch (e) {
        console.log(e);
      } finally {
        this.isConfirmButtonLoading = false;
      }
    },
    async fetchData() {
      this.isFetched = false;
      this.isFetching = true;
      await Promise.all([this.getInspectors(), this.getDataSources({ with_attributes_schema: true }), this.getUsers()]);
      const r = await this.getDatasourceSettings('zdm_inspectors_district');
      if (r !== null) {
        this.datasource = r.datasource;
        this.nameAttribute = r.settings.district_name;
        if (this.datasource) {
          this.features = await this.getDataSourceFeatures({
            dataSource: this.datasource,
            with_geometry: false,
          });
          this.districtItems = cloneDeep(this.inspectors);
        }
      }
      this.isFetching = false;
      this.isFetched = true;
    },
    async init() {
      this.isFetched = false;
      if (this.isDisabled) {
        this.isFetched = true;
      } else {
        await this.fetchData();
      }
    },
  },
  watch: {
    isDisabled(nV) {
      if (!nV) {
        this.fetchData();
      }
    },
  },
  mounted() {
    this.init();
  },
};
</script>

<style scoped lang="scss">
.assignInspector {
  font-family: Roboto;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 16px;
  letter-spacing: 0em;
  text-align: left;
  padding: 0;
}
::v-deep {
  .v-select.v-input--dense .v-chip {
    margin: 2px 4px;
  }
  .v-select__selections {
    flex-wrap: wrap !important;
  }
}
</style>
